<script>
    import { selectedItem } from '../../../store/store.js';
    import { displayInt, calculateNettoskuld, displayBoolean, tooltip, fetchBrf } from '../../../utils/utils.js';
    import CustomSelect from '../../common/CustomSelect.svelte';

    // Reactive derivation of available years from the history
    let availableYears = [];
    $: if ($selectedItem && $selectedItem.core_history && $selectedItem.core_history.history_by_year) {
        availableYears = Object.keys($selectedItem.core_history.history_by_year)
            .map(year => parseInt(year)) // Ensure years are numbers if needed for sorting
            .sort((a, b) => b - a); // Sort descending
    }

    // Handle year selection change from CustomSelect
    function handleYearChange(event) {
        const selectedYear = event.detail; // The value is in event.detail

        // Remove the check against $selectedItem.year, as CustomSelect already handles this
        if (selectedYear) { 
            // Call fetchBrf to get data for the selected year
            console.log(`Fetching data for year: ${selectedYear}`);
            fetchBrf($selectedItem.core_id, selectedYear).catch(error => {
                console.error(`Error fetching data for year ${selectedYear}:`, error);
                // Optionally, handle the error in the UI, e.g., revert selection or show a message
            });
        }
    }

    function displayOwnsTheLand(itemData) {
        if (itemData.rent_for_land != null && itemData.rent_for_land != undefined && itemData.rent_for_land != '' && !Number.isNaN(itemData.rent_for_land) && itemData.rent_for_land > 0){
            return "Nej";
        }
        else if (itemData.owns_the_land == true || (itemData.assessed_value_of_the_land != null && itemData.assessed_value_of_the_land != undefined && itemData.assessed_value_of_the_land != '' && !Number.isNaN(itemData.assessed_value_of_the_land) && itemData.assessed_value_of_the_land > 0)){
            return "Ja";
        }
        else if (itemData.owns_the_land == false) {
            return "Nej";
        }
        else {
            if (itemData.data === null) {
                return "Uppgifter saknas"
            }

            itemData.data.forEach(element => {
                if (element.owns_the_land == true) {
                    return "Ja";
                }
                else if (element.owns_the_land == false) {
                    return "Nej";
                }
            });
        }
        return "Uppgifter saknas";
    }

    // New function to determine value status colors
    function getStatusColor(value, type) {
        if (type === 'loan') {
            if (value < 5000) return 'good';
            if (value < 10000) return 'medium';
            return 'warning';
        }
        return 'neutral';
    }
</script>

<div class="key-value-table box">
    <!-- Wrap CustomSelect in a div styled like the old year-card -->
    <div class="year-card-style-wrapper">
        <CustomSelect 
            options={availableYears}
            bind:selectedValue={$selectedItem.year} 
            on:change={handleYearChange}
            label="År"
            ariaLabel="Välj år"
        />
    </div>
    
    <!-- Row with Äkta förening and Äger marken side by side -->
    <div class="metrics-row">
        <!-- Äkta förening -->
        <div class="metric-card">
            <div class="metric-header">
                <h6>Äkta förening</h6>
                <div class="tooltip-container" use:tooltip>
                    <span class="info-icon"></span>
                    <div class="tooltip-content">
                            <p><strong>Sammanfattning:</strong></p>
                            <ul>
                                <li>En äkta bostadsrättsförening ger fördelar som lägre beskattning vid försäljning.</li>
                                <li>En oäkta bostadsrättsförening kan innebära högre skatt och ibland förmånsbeskattning.</li>
                            </ul>
                            <p><strong>Detaljerade riktlinjer:</strong></p> 
                            <ul> 
                                <li><strong>Äkta bostadsrättsförening (Privatbostadsföretag):</strong></li> 
                                <ul> 
                                    <li>Minst 60% av fastighetens yta ska vara bostäder som upplåts till medlemmarna för att klassas som äkta.</li> 
                                    <li><strong>Fördelar:</strong> Lägre kapitalvinstbeskattning vid försäljning (cirka 22%). Ingen förmånsbeskattning för låg månadsavgift.</li> 
                                    <li><strong>Nackdelar:</strong> Färre intäkter från lokaler eller hyreslägenheter kan innebära högre månadsavgifter för bostadsrättsägare.</li> 
                                </ul>
                                <li><strong>Oäkta bostadsrättsförening (Oäkta bostadsföretag):</strong></li>
                                <ul>
                                    <li>Mer än 40% av intäkterna kommer från hyreslokaler, hyreslägenheter eller juridiska personer som äger bostadsrätter.</li>
                                    <li><strong>Fördelar:</strong> Föreningen kan ha högre hyresintäkter, vilket kan leda till låga eller inga månadsavgifter för bostadsrättsägare.</li>
                                    <li><strong>Nackdelar:</strong> Högre beskattning vid försäljning (25%). Möjlig förmånsbeskattning om månadsavgiften är lägre än en marknadshyra.</li>
                                </ul>
                            </ul>
                    </div>
                </div>
            </div>
            <div class="metric-value">{displayBoolean($selectedItem.genuine_house_association)}</div>
        </div>
        
        <!-- Äger marken -->
        <div class="metric-card">
            <div class="metric-header">
                <h6>Äger marken</h6>
                <div class="tooltip-container" use:tooltip>
                    <span class="info-icon"></span>
                    <div class="tooltip-content">
                            <p><strong>Sammanfattning:</strong></p> 
                            <ul> 
                                <li>Om föreningen äger marken innebär det oftast en stabilare ekonomisk situation.</li> 
                                <li>Tomträtt kan leda till ökade framtida kostnader för föreningen och dess medlemmar.</li> 
                            </ul> 
                            <p><strong>Detaljerade riktlinjer:</strong></p> 
                            <ul> 
                                <li><strong>Föreningen äger marken:</strong></li> 
                                <ul> 
                                    <li>Föreningen har full kontroll över marken och behöver inte betala någon avgift för att nyttja den.</li> 
                                    <li><strong>Fördelar:</strong> Långsiktigt stabilare ekonomi eftersom föreningen inte riskerar avgiftshöjningar kopplade till marken. Eventuella värdeökningar på marken gynnar föreningen och dess medlemmar.</li> 
                                    <li><strong>Nackdelar:</strong> Kan ha ett högre inköpspris initialt, men långsiktigt är det oftast en ekonomiskt tryggare lösning.</li> 
                                </ul>
                                <li><strong>Föreningen har tomträtt (marken hyrs av kommunen):</strong></li>
                                <ul>
                                    <li>Föreningen hyr marken av kommunen och betalar en årlig tomträttsavgäld. Denna avgift omförhandlas vanligtvis var 10-20:e år.</li>
                                    <li><strong>Fördelar:</strong> Lägre kostnad initialt, vilket kan göra att fastigheten är billigare att köpa.</li>
                                    <li><strong>Nackdelar:</strong> Stor risk för avgiftshöjningar vid omförhandling av tomträttsavgälden. Detta kan leda till höjda månadsavgifter för bostadsrättsägare och en osäkrare ekonomisk situation för föreningen.</li>
                                </ul>
                            </ul>
                    </div>
                </div>
            </div>
            <div class="metric-value">{displayOwnsTheLand($selectedItem)}</div>
        </div>
    </div>
    
    <!-- Grid for remaining metrics -->
    <div class="metrics-grid">
        <!-- Lån per kvm -->
        <div class="metric-card">
            <div class="metric-header">
                <h6>Lån per kvm</h6>
                <div class="tooltip-container" use:tooltip>
                    <span class="info-icon"></span>
                    <div class="tooltip-content">
                            <p>Denna siffra baseras på föreningens totala lån och den totala boytan.</p>
                            <p>Det finns ingen exakt regel för vad som är ett "bra" lån per kvm, men här är några allmänna riktlinjer:</p>
                            <p><strong>Sammanfattning av vad man vill se:</strong></p>
                            <ul>
                                <li>Låga lån per kvm (under 5 000 SEK/kvm) är mest eftertraktat för stabilitet och långsiktig trygghet.</li>
                                <li>Måttliga lån per kvm (5 000 - 10 000 SEK/kvm) kan anses rimliga beroende på föreningens planerade underhåll och ekonomiska plan.</li>
                                <li>Höga lån per kvm (över 10 000 SEK/kvm) ökar risken för framtida avgiftshöjningar och kan signalera en tung skuldbörda, vilket kräver en närmare analys av föreningens ekonomiska plan och strategi.</li>
                            </ul>
                            <p><strong>Detaljerade riktlinjer:</strong></p>
                            <p>Det är vanligt att nyare föreningar ofta har lite högre skuldsättning.</p>
                            <p>Om föreningen har intäkter från lokaler, kontor eller andra kommersiella ytor kan en högre skuldsättningsgrad vara mer acceptabel. Detta beror på att dessa ytor inte inkluderas i beräkningen av lån per kvadratmeter, och intäkterna kan bidra till att täcka föreningens kostnader och minska risken för avgiftshöjningar.</p>
                                
                            <ul>
                                <li><strong>Lågt lån per kvm (0 – 5 000 SEK/kvm):</strong></li>
                                <ul>
                                <li>Föreningar med låga lån har ofta en sund ekonomi och större utrymme för att möta framtida underhåll och räntehöjningar utan att behöva höja avgifterna dramatiskt. Det kan vara ett tecken på att föreningen har betalat av sina lån eller har finansierat större underhåll utan att ta på sig mycket skuld.</li>
                                <li><strong>Fördel:</strong> Stabilare ekonomi, lägre risk för avgiftshöjningar.</li>
                                <li><strong>Nackdel:</strong> Kan signalera att avgifterna är för höga eller att föreningen inte har investerat tillräckligt i fastigheten.</li>
                                </ul>
                
                                <li><strong>Måttligt lån per kvm (5 000 – 10 000 SEK/kvm):</strong></li>
                                <ul>
                                <li>Detta anses ofta som en sund nivå för föreningar som gör kontinuerliga renoveringar och underhåll. Lån i denna nivå kan indikera att föreningen har tagit lån för större renoveringar (t.ex. stambyten eller fasadrenoveringar), men att skuldbördan fortfarande är hanterbar.</li>
                                <li><strong>Fördel:</strong> Bra balans mellan investeringar och skuldbörda.</li>
                                <li><strong>Nackdel:</strong> Liten risk för framtida avgiftshöjningar beroende på ränteutvecklingen och nya underhållsbehov.</li>
                                </ul>
                
                                <li><strong>Högt lån per kvm (över 10 000 SEK/kvm):</strong></li>
                                <ul>
                                <li>Om lån per kvm överstiger 10 000 SEK kan det vara en varningssignal. Det indikerar att föreningen har en hög skuldbörda i förhållande till sin boyta, vilket ökar risken för framtida avgiftshöjningar, speciellt vid räntehöjningar eller oförutsedda utgifter.</li>
                                <li><strong>Fördel:</strong> Föreningen kan ha finansierat viktiga renoveringar.</li>
                                <li><strong>Nackdel:</strong> Stor risk för höga avgiftshöjningar om föreningen inte hanterar sin skuldbörda och framtida underhåll väl.</li>
                                </ul>
                            </ul>
                    </div>
                </div>
            </div>
            <div class="metric-value">
                <span class="value-with-indicator {getStatusColor($selectedItem.living_area_sqm > 0 ? Math.ceil($selectedItem.total_amount_of_loans / $selectedItem.living_area_sqm) : 0, 'loan')}">
                    {displayInt($selectedItem.living_area_sqm > 0 ? Math.ceil($selectedItem.total_amount_of_loans / $selectedItem.living_area_sqm) : "-")} kr/m²
                </span>
            </div>
        </div>
        
        <!-- Indirekt nettoskuldsättning -->
        <div class="metric-card">
            <div class="metric-header">
                <h6>Indirekt nettoskuldsättning</h6>
                <div class="tooltip-container" use:tooltip>
                    <span class="info-icon"></span>
                    <div class="tooltip-content">
                            <p><strong>Indirekt nettoskuldsättning:</strong></p>
                            <ul>
                                <li>Föreningen kan ha pengar som kan användas för att betala av lån eller andra utgifter. Indirekt nettoskuldsättning innehåller denna information och kan då ge en bättre bild på skuldläget.</li>
                                <li>Beräkningen baseras på föreningens räntebärande skulder, räntebärande tillgångar och likvida medel.</li>
                                <li>För att få ut lägenhetens indirekta nettoskuldsättning multiplicerar man detta med bostadsrättens andelstal för årsavgift.</li>
                            </ul>
                            <p><strong>Poster som ingår i beräkningen:</strong></p>
                            <p>Räntebärande skulder</p>
                            <ul>
                                <li>Lång- och kortfristiga skulder till kreditinstitut</li>
                                <li>Banklån</li>
                                <li>Övriga skulder, t.ex. till fastighetsbolag och utvecklare</li>
                            </ul>
                            <p>Räntebärande tillgångar</p>
                            <ul>
                                <li>Kortfrisitga placeringar</li>
                                <li>Fastränteplaceringar</li>
                            </ul>
                            <p>Likvida medel</p>
                            <ul>
                                <li>Kassa och bank</li>
                                <li>Klientmedels-/avräkningskonto</li>
                                <li>Skattekonto</li>
                            </ul>
                            <p><strong>Poster som inte ska beräknas men som bör kommenteras om de överstiger 500 000 kr:</strong></p>
                            <ul>
                                <li>Övriga skulder</li>
                                <li>Övriga fordringar</li>
                                <li>Finansiella anläggningstillgångar</li>
                            </ul>
                            <p><strong>Ekvation:</strong></p>
                            <p>Total summa lån - Likvida medel vid årets slut - Skattekonto - Klientmedel</p>
                    </div>
                </div>
            </div>
            <div class="metric-value">
                <span class="value-with-indicator {getStatusColor($selectedItem.living_area_sqm > 0 ? Math.ceil(calculateNettoskuld($selectedItem.total_amount_of_loans, $selectedItem.cash_at_the_end_of_the_year, $selectedItem.skattekonto, $selectedItem.klientmedel, $selectedItem.kassa_och_bank) / $selectedItem.living_area_sqm) : 0, 'loan')}">
                    {displayInt($selectedItem.living_area_sqm > 0 ? Math.ceil(calculateNettoskuld($selectedItem.total_amount_of_loans, $selectedItem.cash_at_the_end_of_the_year, $selectedItem.skattekonto, $selectedItem.klientmedel, $selectedItem.kassa_och_bank) / $selectedItem.living_area_sqm) : "-")} kr/m²
                </span>
            </div>
        </div>

        <!-- TODO: Vill man använda denna?
        {#if $selectedItem.risk_for_fee_increase}
        <div class="metric-card">
            <div class="metric-header">
                <h6>Risk för höjning av avgift</h6>
            </div>
            <div class="metric-value">
                { displayBoolean($selectedItem.risk_for_fee_increase) }
            </div>
        </div>
        {/if}
        -->
    </div>
</div>

<style>
    /* Add styles for the new wrapper, mimicking the old year-card */
    .year-card-style-wrapper {
        background: linear-gradient(135deg, var(--box-title-color), #343a40);
        color: white; /* Ensures label color inherits correctly if needed */
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 20px;
        text-align: center;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        position: relative; /* Keep for potential absolute positioning inside */
    }

    .value-with-indicator {
        position: relative;
        padding-left: 8px;
        border-left: 4px solid transparent;
    }
    
    .value-with-indicator.good {
        border-left-color: #28a745;
    }
    
    .value-with-indicator.medium {
        border-left-color: #ffc107;
    }
    
    .value-with-indicator.warning {
        border-left-color: #dc3545;
    }
    
    .value-with-indicator.neutral {
        border-left-color: #6c757d;
    }
</style>